import React, { useEffect, useState } from 'react'
import { useLocation, Link } from 'react-router-dom'
import { Card } from 'primereact/card'
import { Button } from 'primereact/button'

const RegistrationSuccess: React.FC = () => {
  const location = useLocation()
  const [message, setMessage] = useState<string>('')

  useEffect(() => {
    const state = location.state as { message?: string; type?: string } | null
    if (state?.message && state?.type === 'success') {
      setMessage(state.message)
      // Clear the location state to prevent the message from persisting on refresh
      window.history.replaceState({}, '', location.pathname)
    } else {
      // Default message if no state is provided (direct navigation)
      setMessage('Your registration has been submitted successfully!')
    }
  }, [location])

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-green-50 via-white to-blue-50 py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-2xl w-full space-y-8">
        <div className="text-center">
          <div className="mx-auto flex items-center justify-center h-20 w-20 rounded-full bg-green-100 mb-6">
            <i className="pi pi-check-circle text-green-600 text-4xl"></i>
          </div>
          <h1 className="text-4xl font-extrabold text-gray-900 mb-4">
            🎉 Welcome to Our Community!
          </h1>
          <p className="text-xl text-gray-600">
            Thank you for your interest in joining our organization
          </p>
        </div>

        <Card className="shadow-2xl border-0">
          <div className="bg-gradient-to-r from-green-600 to-blue-600 p-8 rounded-t-lg">
            <h2 className="text-2xl font-bold text-white mb-2">
              Registration Successful
            </h2>
            <p className="text-green-100">
              Your membership application has been received
            </p>
          </div>

          <div className="p-8 space-y-6">
            <div className="bg-green-50 border-l-4 border-green-400 p-6 rounded-r-lg">
              <div className="flex">
                <div className="flex-shrink-0">
                  <i className="pi pi-info-circle text-green-400 text-xl"></i>
                </div>
                <div className="ml-3">
                  <p className="text-green-800 font-medium mb-2">{message}</p>
                </div>
              </div>
            </div>

            <div className="space-y-4">
              <h3 className="text-lg font-semibold text-gray-900 flex items-center">
                <i className="pi pi-clock mr-2 text-blue-600"></i>
                What happens next?
              </h3>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                <div className="text-center p-4 bg-blue-50 rounded-lg">
                  <div className="w-12 h-12 mx-auto bg-blue-100 rounded-full flex items-center justify-center mb-3">
                    <span className="text-blue-600 font-bold text-lg">1</span>
                  </div>
                  <h4 className="font-medium text-gray-900 mb-2">Review Process</h4>
                  <p className="text-sm text-gray-600">
                    Our admin team will carefully review your application
                  </p>
                </div>

                <div className="text-center p-4 bg-green-50 rounded-lg">
                  <div className="w-12 h-12 mx-auto bg-green-100 rounded-full flex items-center justify-center mb-3">
                    <span className="text-green-600 font-bold text-lg">2</span>
                  </div>
                  <h4 className="font-medium text-gray-900 mb-2">Email Confirmation</h4>
                  <p className="text-sm text-gray-600">
                    You'll receive a confirmation email with next steps
                  </p>
                </div>

                <div className="text-center p-4 bg-purple-50 rounded-lg">
                  <div className="w-12 h-12 mx-auto bg-purple-100 rounded-full flex items-center justify-center mb-3">
                    <span className="text-purple-600 font-bold text-lg">3</span>
                  </div>
                  <h4 className="font-medium text-gray-900 mb-2">Welcome!</h4>
                  <p className="text-sm text-gray-600">
                    Once approved, we'll welcome you to our community
                  </p>
                </div>
              </div>
            </div>

            <div className="border-t pt-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">
                While you wait...
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="p-4 border border-gray-200 rounded-lg hover:border-blue-300 transition-colors">
                  <h4 className="font-medium text-gray-900 mb-2 flex items-center">
                    <i className="pi pi-envelope mr-2 text-blue-600"></i>
                    Check Your Email
                  </h4>
                  <p className="text-sm text-gray-600">
                    Make sure to check your email (including spam folder) for updates on your application status.
                  </p>
                </div>

                <div className="p-4 border border-gray-200 rounded-lg hover:border-green-300 transition-colors">
                  <h4 className="font-medium text-gray-900 mb-2 flex items-center">
                    <i className="pi pi-users mr-2 text-green-600"></i>
                    Learn About Us
                  </h4>
                  <p className="text-sm text-gray-600">
                    Take some time to learn more about our community, values, and upcoming events.
                  </p>
                </div>
              </div>
            </div>

            <div className="bg-gray-50 p-6 rounded-lg">
              <div className="flex items-start">
                <div className="flex-shrink-0">
                  <i className="pi pi-question-circle text-gray-400 text-xl"></i>
                </div>
                <div className="ml-3">
                  <h4 className="font-medium text-gray-900 mb-2">Questions?</h4>
                  <p className="text-sm text-gray-600 mb-3">
                    If you have any questions about your application or our organization,
                    feel free to reach out to our team.
                  </p>
                  <p className="text-sm text-gray-500">
                    We typically respond to inquiries within 1-2 business days.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </Card>

        <div className="text-center space-y-4">
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/register">
              <Button
                label="Register Another Member"
                icon="pi pi-user-plus"
                className="p-button-outlined"
              />
            </Link>
          </div>

          <p className="text-sm text-gray-500">
            Reference ID: {Date.now().toString().slice(-6)} •
            Registration submitted on {new Date().toLocaleDateString()}
          </p>
        </div>
      </div>
    </div>
  )
}

export default RegistrationSuccess